import React, { Fragment, useEffect, useState } from "react";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../common/breadcrumb";
import axiosInstance from "../../utils/axios";
import ToggleSwitch from "../../shared/ToggleSwitch";

const EditPromotion = () => {
    const navigate = useNavigate();
    const { promotionId } = useParams();
    const [isFetching, setIsFetching] = useState(false);
    const [promotionData, setPromotionData] = useState({
        promotionName: "",
        description: "",
        startDate: "",
        endDate: "",
        isFeatured: false,
        isActive: true,
    });
    const [imageForPayload, setImageForPayload] = useState(null);
    const [dummyImg, setDummyImg] = useState("https://via.placeholder.com/50");

    useEffect(() => {
        const fetchPromotion = async () => {
            setIsFetching(true);
            try {
                const response = await axiosInstance.get("/api/promotion/getAllPromotions");
                const promo = response.data.data.promotions.find((p) => p.id === parseInt(promotionId));
                if (promo) {
                    setPromotionData({
                        promotionName: promo.promotionName,
                        description: promo.description || "",
                        startDate: promo.startDate.split("T")[0],
                        endDate: promo.endDate.split("T")[0],
                        isFeatured: promo.isFeatured || false,
                        isActive: promo.active || false,
                    });
                    const promotionImageUrl = promo.image ? `${process.env.REACT_APP_URL}${promo.image}` : "https://via.placeholder.com/50";
                    setDummyImg(promotionImageUrl);
                }
            } catch (error) {
                console.error("Error fetching promotion:", error.response?.data || error);
            } finally {
                setIsFetching(false);
            }
        };
        fetchPromotion();
    }, [promotionId]);

    const handleInputChange = (e, key) => {
        setPromotionData((prev) => ({ ...prev, [key]: e.target.value }));
    };

    const handleFeaturedChange = (e) => {
        setPromotionData((prev) => ({ ...prev, isFeatured: e.target.value === "true" }));
    };

    const _handleImgChange = (e) => {
        e.preventDefault();
        const image = e.target.files[0];
        setImageForPayload(image);
        let reader = new FileReader();
        reader.onload = () => setDummyImg(reader.result);
        reader.readAsDataURL(image);
    };

    const handleValidSubmit = async (e) => {
        e.preventDefault();
        setIsFetching(true);
        try {
            const formData = new FormData();
            formData.append("promotionName", promotionData.promotionName);
            formData.append("description", promotionData.description);
            formData.append("startDate", promotionData.startDate);
            formData.append("endDate", promotionData.endDate);
            formData.append("isFeatured", promotionData.isFeatured);
            formData.append("active", promotionData.isActive);
            formData.append("dataType", "promotion");
            if (imageForPayload) formData.append("image1", imageForPayload);

            const response = await axiosInstance.put(
                `/api/promotion/updatePromotion/${promotionId}`,
                formData,
                { rawRequest: true }
            );

            if (response.status === 200 && !response.data.error) {
                navigate("/promotion/promotion-list");
            } else {
                console.error("Error updating promotion:", response.data.message);
            }
        } catch (error) {
            console.error("Error updating promotion:", error.response?.data || error);
        } finally {
            setIsFetching(false);
        }
    };

    return (
        <Fragment>
            <Breadcrumb title="Edit Promotion" parent="Promotions" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader>
                                <h5>Edit Promotion</h5>
                            </CardHeader>
                            <CardBody>
                                {isFetching ? (
                                    <div>Updating promotion...</div>
                                ) : (
                                    <Row className="product-adding">
                                        <Col xl="5">
                                            <div className="add-product">
                                                <Input type="file" className="upload" onChange={_handleImgChange} />
                                                <img src={dummyImg} alt="promotion" style={{ width: 50, height: 50 }} />
                                            </div>
                                        </Col>
                                        <Col xl="7">
                                            <Form className="needs-validation" onSubmit={handleValidSubmit}>
                                                <FormGroup className="form-group mb-3 row">
                                                    <Label className="col-xl-3 col-sm-4 mb-0">Name:</Label>
                                                    <div className="col-xl-8 col-sm-7">
                                                        <Input
                                                            type="text"
                                                            value={promotionData.promotionName}
                                                            onChange={(e) => handleInputChange(e, "promotionName")}
                                                            required
                                                        />
                                                    </div>
                                                </FormGroup>
                                                <FormGroup className="form-group mb-3 row">
                                                    <Label className="col-xl-3 col-sm-4 mb-0">Description:</Label>
                                                    <div className="col-xl-8 col-sm-7">
                                                        <Input
                                                            type="textarea"
                                                            value={promotionData.description}
                                                            onChange={(e) => handleInputChange(e, "description")}
                                                        />
                                                    </div>
                                                </FormGroup>
                                                <FormGroup className="form-group mb-3 row">
                                                    <Label className="col-xl-3 col-sm-4 mb-0">Start Date:</Label>
                                                    <div className="col-xl-8 col-sm-7">
                                                        <Input
                                                            type="date"
                                                            value={promotionData.startDate}
                                                            onChange={(e) => handleInputChange(e, "startDate")}
                                                            required
                                                        />
                                                    </div>
                                                </FormGroup>
                                                <FormGroup className="form-group mb-3 row">
                                                    <Label className="col-xl-3 col-sm-4 mb-0">End Date:</Label>
                                                    <div className="col-xl-8 col-sm-7">
                                                        <Input
                                                            type="date"
                                                            value={promotionData.endDate}
                                                            onChange={(e) => handleInputChange(e, "endDate")}
                                                            required
                                                        />
                                                    </div>
                                                </FormGroup>
                                                <FormGroup className="form-group mb-3 row">
                                                    <Label className="col-xl-3 col-sm-4 mb-0">Featured:</Label>
                                                    <div className="col-xl-8 col-sm-7">
                                                        <Input
                                                            type="select"
                                                            value={promotionData.isFeatured}
                                                            onChange={handleFeaturedChange}
                                                        >
                                                            <option value={true}>Yes</option>
                                                            <option value={false}>No</option>
                                                        </Input>
                                                    </div>
                                                </FormGroup>
                                                <FormGroup className="form-group mb-3 row">
                                                    <Label className="col-xl-3 col-sm-4 mb-0">
                                                        Active:
                                                    </Label>
                                                    <div className="col-xl-8 col-sm-7">
                                                        <ToggleSwitch
                                                            label="Toggle Switch"
                                                            checked={promotionData?.isActive || false}
                                                            handleToggle={(checked) => setPromotionData((prev) => ({ ...prev, isActive: checked }))}
                                                        />
                                                    </div>
                                                </FormGroup>
                                                <div className="offset-xl-3 offset-sm-4">
                                                    <Button color="primary" type="submit">
                                                        Update
                                                    </Button>
                                                    <Button
                                                        color="danger"
                                                        onClick={() => navigate("/promotion/promotion-list")}
                                                    >
                                                        Discard
                                                    </Button>
                                                </div>
                                            </Form>
                                        </Col>
                                    </Row>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default EditPromotion;